<template>
  <div>
    <table-header
      name="Hedberg Table"
      :fields="exportFields"
      :is-drilldown="false"
      @export-pdf="exportPDF(columns, body, 'orderquotefinancials.pdf')"
      @export-xlsx="exportXLSX(body, 'orderquotefinancials.csv')"
    >
      <template #reset-button>
        <b-button
          v-if="Object.keys(filters).length > 0"
          size="is-small"
          icon="power-off"
          @click="resetFilters"
        >
          Reset Filters
        </b-button>
      </template>

      <template #customize>
        <b-dropdown
          aria-role="list"
          postition="is-bottom-left"
        >
          <b-button
            slot="trigger"
            size="is-small"
            icon-left="wrench"
            icon-right="chevron-down"
          >
            grids
          </b-button>

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.isBordered"
                size="is-small"
              >
                Bordered
              </b-checkbox>
            </b-field>
          </b-dropdown-item>
          <hr class="dropdown-divider">

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.isCheckable"
                size="is-small"
              >
                Checkable
              </b-checkbox>
            </b-field>
          </b-dropdown-item>
          <hr class="dropdown-divider">

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.isNarrowed"
                size="is-small"
              >
                Narrowed
              </b-checkbox>
            </b-field>
          </b-dropdown-item>
          <hr class="dropdown-divider">

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.stickyHeaders"
                size="is-small"
              >
                Sticky headers
              </b-checkbox>
            </b-field>
          </b-dropdown-item>
          <hr class="dropdown-divider">

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.isScrollable"
                size="is-small"
              >
                Scrollable
              </b-checkbox>
            </b-field>
          </b-dropdown-item>

          <hr class="dropdown-divider">

          <b-dropdown-item aria-role="listitem">
            <b-field>
              <b-checkbox
                v-model="customizations.isStriped"
                size="is-small"
              >
                Striped
              </b-checkbox>
            </b-field>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template #hide-show-columns>
        <b-dropdown
          aria-role="list"
          postition="is-bottom-left"
          :scrollable="true"
          :max-height="300"
          :close-on-click="false"
          class="dg-columns"
        >
          <b-button
            slot="trigger"
            icon-left="eye"
            size="is-small"
            icon-right="chevron-down"
          >
            columns
          </b-button>
          <draggable
            v-model="columns"
            group="columns"
            handle=".handle"
            @start="drag=true"
            @end="drag=false"
          >
            <b-dropdown-item
              v-for="(column, index) in columns"
              :key="index"
              aria-role="listitem"
            >
              <b-field>
                <b-checkbox
                  :id="column.field"
                  v-model="column.visible"
                  size="is-small"
                >
                  {{ column.label }}
                </b-checkbox>
                <i class="grip-vertical handle" />
              </b-field>
            </b-dropdown-item>
          </draggable>
          <hr class="dropdown-divider">
        </b-dropdown>
      </template>
    </table-header>

    <b-table
      ref="
  hedbergTable"
      :mobile-cards="false"
      :data="orderQuoteFinancials"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      :narrowed="customizations.isNarrowed"
      :bordered="customizations.isBordered"
      :striped="customizations.isStriped"
      :sticky-header="customizations.stickyHeaders"
      :scrollable="customizations.isScrollable"
      :sort-icon="sortIcon"
      :sort-icon-size="sortIconSize"
    >
      <b-table-column
        v-for="(column, index) in columns"
        :key="index"
        :label="column.label"
        :field="column.field"
        :searchable="column.searchable"
        :sortable="column.sortable"
        :visible="column.visible"
      >
        <template
          slot="searchable"
          slot-scope="props"
        >
          <b-input
            v-model="props.filters[`${column.searchField}`]"
            placeholder="Search..."
            icon="search"
            size="is-small"
          />
        </template>

        <template v-slot="props">
          {{ props.row[column.field] }}
        </template>
      </b-table-column>

      <template slot="footer">
        <!-- TODO: Enable on next sprint -->
        <!-- <th
          v-for="(option, index) in Object.values(orderQuoteFinancialsSummation)"
          :key="index"
        >
          {{ option }}
        </th> -->
      </template>

      <template slot="empty">
        <empty-table table-name="opportunities" />
      </template>
    </b-table>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import { TableHeader, EmptyTable } from '@/components/Shared';

export default {
  name: 'OpportunitiesHedbergTable',

  components: {
    TableHeader,
    EmptyTable,
    draggable,
  },

  data: () => ({
    exportFields: {},

    columns: [
      {
        label: 'Project ID',
        field: 'project_id',
        searchField: 'projectID',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Project Title',
        field: 'project_title',
        searchField: 'projectTitle',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Quoted Sell',
        field: 'quoted_sell',
        searchField: 'quotedSell',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Quoted GP$',
        field: 'quoted_gp',
        searchField: 'quotedGP',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Quoted GP%',
        field: 'quoted_gp_percentage',
        searchField: 'quotedGrossProfitPercent',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Project Sell',
        field: 'project_sell',
        searchField: 'projectSell',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Project GP$',
        field: 'project_gp',
        searchField: 'projectGP',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Project GP%',
        field: 'project_gp_percentage',
        searchField: 'projectGrossProfitPercent',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Invoiced Sell',
        field: 'invoiced_sell',
        searchField: 'invoicedSell',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Invoiced GP$',
        field: 'invoiced_gp',
        searchField: 'invoicedGP',
        searchable: true,
        sortable: true,
        visible: true,
      },
      {
        label: 'Invoiced GP%',
        field: 'invoiced_gp_percentage',
        searchField: 'invoicedGrossProfitPercent',
        searchable: true,
        sortable: true,
        visible: true,
      },
    ],

    checkedRows: [],

    total: 0,
    page: 1,
    perPage: 50,

    sortIcon: 'menu-up',
    sortIconSize: 'is-small',

    isEditing: false,
    selectedRecordId: null,

    filters: {},

    customizations: {
      isBordered: false,
      isCheckable: false,
      isNarrowed: false,
      isStickyHeaders: false,
      isScrollable: true,
      isStriped: true,
    },
  }),

  computed: {
    ...mapGetters({
      orderQuoteFinancials: 'OrderQuoteFinancials/getOrderQuoteFinancials',
      orderQuoteFinancialsSummation: 'OrderQuoteFinancials/getOrderQuoteFinancialsSummation',
    }),

    firstItem() {
      const firstItem = this.page * this.perPage - this.perPage + 1;
      return firstItem >= 0 ? firstItem : 0;
    },
  },

  mounted() {
    this.fetchOrderQuoteFinancials();
    this.$store.dispatch(
      'OrderQuoteFinancials/fetchOrderQuoteFinancialsSummation',
      this.$route.params.opportunityId,
    );
  },

  methods: {
    async fetchOrderQuoteFinancials() {
      try {
        await this.$store.dispatch(
          'OrderQuoteFinancials/fetchOrderQuoteFinancials',
          this.$route.params.opportunityId,
        );
      } catch (error) {
        console.error(error);
      }
    },

    getTotal(field) {
      const sum = this.orderQuoteFinancials.map((el) => el[field]).reduce((a, b) => a + b, 0);
      return sum;
    },
  },
};
</script>

<style>

</style>
